import React, { useEffect, useState } from "react";
import { BodyPartPinType } from "./BodyPartsStats";
import leftToRightImage from "../assets/images/from-l-to-r.webp";
import rightToLeftImage from "../assets/images/from-r-to-l.webp";
import Text from "../../../components/ui/Text";
import { useAudioEffects } from "../../../hooks/use-audio-effects";
import { motion } from "framer-motion";
import dropAudio from "../../../assets/audio/drop.wav";
import zoomAudio from "../../../assets/audio/zoom.wav";

type HoverInfoProps = {
  title: string;
  bleedingText: string;
  bleedingPercent: number;
  paragraph: string;
  x: number;
  y: number;
};

const HoverInfo: React.FC<HoverInfoProps> = ({
  title,
  bleedingText,
  bleedingPercent,
  paragraph,
  x,
  y,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className={`bg-black/60 rounded-md p-5 px-6 absolute top-[50%] translate-y-[-70%] min-w-[280px] shadow-lg shadow-black/40
      ${
        y < 50
          ? "left-[50%] translate-x-[-50%] !top-[-60%]"
          : x >= 50
          ? "left-[117%]"
          : "right-[117%]"
      }`}
    >
      <h2 className="uppercase text-[15px] font-semibold text-white">
        {title}
      </h2>

      <div className="flex items-center space-x-2 mt-3 mb-2">
        <p className="text-white text-[14px]">{bleedingText}</p>
        <span className="p-1 px-2 rounded-sm bg-[red]/10 text-[13px] text-red-500">
          {bleedingPercent}%
        </span>
      </div>

      <p className="text-white/70 text-[12px]">{paragraph}</p>
    </motion.div>
  );
};

type BodyPartPinProps = {
  className?: string;
  variant: 1 | 2;
} & BodyPartPinType;

const BodyPartPin: React.FC<BodyPartPinProps> = ({
  name,
  value,
  x,
  y,
  image,
  className = "",
  direction,
  variant,
}) => {
  const { playEffect } = useAudioEffects();
  const [draggingOver, setDraggingOver] = useState<boolean>(false);
  const [hovering, setHovering] = useState<boolean>(false);

  //  Play zoom effect while item is being dragged over this pin
  useEffect(() => {
    if (draggingOver) {
      playEffect(zoomAudio, { volume: 0.2 });
    }
  }, [draggingOver]);

  /** Handles when a medical item is dragged over */
  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDraggingOver(true);
    setHovering(true);
    e.dataTransfer.dropEffect = "move";
  }

  /** Handles when a medical item is dropped */
  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDraggingOver(false);

    const medicalItem = JSON.parse(e.dataTransfer.getData("text/plain"));
    if (!medicalItem)
      throw new Error("[ON_DROP]/BodyPartPin: ERROR - There's no medical item");

    playEffect(dropAudio, { volume: 0.66 });

    console.log(medicalItem);

    // Here developer can do whatever he needs to do with this medical item
  }

  /** Reset styling once drag leaves area */
  function onDragLeave() {
    setDraggingOver(false);
    setHovering(false);
  }

  // ✅ VARIANT 1 — Has hover info and drag functionality
  if (variant === 1) {
    return (
      <div
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragLeave={onDragLeave}
        onMouseOver={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        className={`select-none gilroy-font absolute transform -translate-x-1/2 -translate-y-1/2 transition 
        ${draggingOver && "animate-pulse scale-125"} ${className}`}
        style={{ left: `${x}%`, top: `${y}%` }}
      >
        {/* Label section */}
        <div
          className={`w-full flex relative mb-[-17px] 
          ${
            direction === "from-l-to-r"
              ? "justify-start ml-[-10px]"
              : "justify-end ml-[10px]"
          }`}
        >
          <div className="max-w-fit">
            <Text
              size="xs"
              className={`text-white !font-semibold ${
                direction === "from-l-to-r" ? "text-left" : "text-right"
              }`}
            >
              {name}
            </Text>

            <Text
              size="xs"
              className={`text-[#47FF94] !font-[400] 
              ${direction === "from-l-to-r" ? "text-right" : "text-left"}`}
            >
              {value}%
            </Text>
          </div>
        </div>

        {/* Connector line */}
        <img
          src={
            image ||
            (direction === "from-l-to-r" ? leftToRightImage : rightToLeftImage)
          }
          alt="Line"
          className="w-[150px] h-[46px] object-contain z-[-1] pointer-events-none select-none"
        />

        {/* ✅ Extracted Hover Component */}
        {hovering && (
          <HoverInfo
            title={`Inspecteeaza ${x}`}
            bleedingText="Sangare"
            bleedingPercent={80}
            paragraph="Upgrade your pilot skills in this minigame."
            x={x}
            y={y}
          />
        )}
      </div>
    );
  }

  // ✅ VARIANT 2 — No hover info
  if (variant === 2) {
    return (
      <div
        className={`select-none gilroy-font absolute transform -translate-x-1/2 -translate-y-1/2 flex items-center 
        ${
          direction === "from-l-to-r" ? "flex-row" : "flex-row-reverse"
        } ${className}`}
        style={{ left: `${x}%`, top: `${y}%` }}
      >
        <img src={image} alt="Required img" />
        <div className="max-w-fit">
          <Text
            size="xs"
            className={`text-white !font-semibold ${
              direction === "from-l-to-r" ? "text-left" : "text-right"
            }`}
          >
            {name}
          </Text>

          <Text
            size="xs"
            className={`text-[#47FF94] !font-[400] 
            ${direction === "from-l-to-r" ? "text-right" : "text-left"}`}
          >
            {value}%
          </Text>
        </div>
      </div>
    );
  }

  return null;
};

export default BodyPartPin;
